// 🛡️ Project: LearningPortal
// 📅 Created: 2025
// 👤 Author: Dmitriy P.A.
// 🔒 Proprietary Code – do not copy without permission.

import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import SearchBar from "./SearchBar";
import Loading from "./Loading";
import TiptapEditor from "./TiptapEditor";
import NewsRenderer from "./NewsRenderer";
import { useAttachments } from "./hooks/useAttachments";
import AttachmentsInput from "./AttachmentInput";
import DraggablePublishModal from "./DraggablePublishModal";
import DraggableFileViewer from "./DraggableFileViewer";
import "./styles/MainPage.css";
import { motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import LikeButton from "./LikeButton";

const premiumTexts = [
  {
    title: "Открой новые возможности с LearningPortal Premium",
    description:
      "Получи доступ к автопроверке ДЗ с помощью ИИ, подробным графикам успеваемости, уникальному оформлению профиля и другим премиум-функциям.",
  },
  {
    title: "Прокачай свой учебный опыт!",
    description:
      "Premium-аккаунт откроет для тебя новые горизонты: умная проверка заданий, аналитика по предметам, эксклюзивные темы и бонусы профиля.",
  },
  {
    title: "Premium — всё лучшее в одном аккаунте.",
    description: "Успеваемость. ИИ. Стиль. Контроль.",
  },
  {
    title: "Сделай учёбу удобнее и интереснее.",
    description:
      "С премиум-доступом ты получаешь больше: умные функции, индивидуальные графики, красивые профили и удобство на каждом шагу.",
  },
];

const MainPage = ({ user, token, apiUrl }) => {
  // 1) Хук для НОВОЙ публикации:
  const {
    attachments: pubAtt,
    uploadFile: uploadPub,
    removeAttachment: removePub,
    reorder: reorderPub,
    setAttachments: setPubAtt,
  } = useAttachments(apiUrl, token);
  // 2) Хук для РЕДАКТИРОВАНИЯ текущего элемента:
  const {
    attachments: editAtt,
    uploadFile: uploadEdit,
    removeAttachment: removeEdit,
    reorder: reorderEdit,
    setAttachments: setEditAtt,
  } = useAttachments(apiUrl, token);
  const [viewer, setViewer] = useState({ open: false, url: "", mime: "" });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightRef = useRef(null);
  const [news, setNews] = useState([]);
  const [postNews, setPostNews] = useState({ title: "", content: "" });
  const [editingItem, setEditingItem] = useState(null);
  const [newNews, setNewNews] = useState([]);
  const [marked, setMarked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [editForm, setEditForm] = useState({
    title: "",
    content: "",
    attachments: editAtt || [],
  });
  const [text] = useState(() => {
    return premiumTexts[Math.floor(Math.random() * premiumTexts.length)];
  });

  const isAuthorized =
    user &&
    (user.role === "director" ||
      user.role === "editor" ||
      user.role === "admin");

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const timer = setTimeout(() => {
        highlightRef.current?.classList.remove("highlighted");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightId]);

  useEffect(() => {
    fetchNews();
  }, [token, apiUrl, user, marked]);

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/news`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allNews = res.data;
      setNews(allNews);

      // ОПРЕДЕЛЯЕТ НОВЫЙ СООБЩЕНИЯ (непрочитанные)
      const unread = allNews.filter(
        (n) => !(user.readNews || []).includes(n._id)
      );
      setNewNews(unread);

      // ОТМЕЧАЕТ СООБЩЕНИЕ КАК ПРОЧТЕННОЕ
      if (unread.length > 0 && !marked) {
        const unreadNewsIds = unread.map((n) => n._id);

        await axios.post(
          `${apiUrl}/api/news/mark-read`,
          { newsIds: unreadNewsIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const updatedUser = {
          ...user,
          readNews: [...(user.readNews || []), ...unreadNewsIds],
        };
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
        //setUser(updatedUser);
        setMarked(true);
      }
    } catch (err) {
      console.error("Ошибка при загрузке новостей:", err);
    } finally {
      setLoading(false);
    }
  };

  const submitEdit = async () => {
    try {
      const payload = {
        title: editForm.title,
        content: editForm.content,
        attachments: editAtt.map(({ url, mime }) => ({ url, mime })),
      };

      const res = await axios.put(
        `${apiUrl}/api/news/${editingItem._id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNews((prev) =>
        prev.map((n) => (n._id === res.data._id ? res.data : n))
      );
      setEditingItem(null);
    } catch (err) {
      console.error("Ошибка при редактировании", err);
    }
  };

  // PUBLISH

  const handlePublish = async () => {
    try {
      const payload = {
        title: postNews.title,
        content,
        attachments: pubAtt.map(({ url, mime }) => ({ url, mime })),
      };
      await axios.post(`${apiUrl}/api/news`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPubAtt([]);
      setShowPublishModal(false);
      fetchNews();
    } catch (err) {
      console.error("Ошибка при публикации новости", err);
    }
    fetchNews();
  };

  // BUTTONS

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/api/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNews(news.filter((n) => n._id !== id));
    } catch (err) {
      console.error("Ошибка при удалении новости", err);
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setEditForm({
      title: item.title,
      content: item.content,
    });
    setEditAtt(
      (item.attachments || []).map((a) => ({
        id: uuidv4(),
        url: a.url,
        mime: a.mime,
      }))
    );
  };

  const toggleLike = async (id) => {
    try {
      const res = await axios.post(
        `${apiUrl}/api/news/${id}/like`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNews((prevNews) =>
        prevNews.map((n) =>
          n._id === id
            ? {
                ...n,
                likes: res.data.liked
                  ? [...(n.likes || []), user._id]
                  : (n.likes || []).filter((uid) => uid !== user._id),
              }
            : n
        )
      );
    } catch (err) {
      console.error("Ошибка при выставлении/снятии лайка");
    }
  };

  // RETURN
  return (
    <motion.div
      className="news-page glass-card"
      initial={{ opacity: 1, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="news-title" style={{ marginTop: "0" }}>
        Новости
      </h2>
      <SearchBar token={token} apiUrl={apiUrl} user={user} />
      {/* Запостить новое сообщение */}
      {isAuthorized && (
        <>
          <motion.button
            onClick={() => setShowPublishModal(true)}
            className="publish-toggle"
          >
            {!showPublishModal ? "+ Добавить публикацию" : "Окно открыто"}
          </motion.button>
          {showPublishModal && (
            <DraggablePublishModal onClose={() => setShowPublishModal(false)}>
              <AttachmentsInput
                attachments={pubAtt}
                uploadFile={uploadPub}
                removeAttachment={removePub}
                reorder={reorderPub}
              />
              <input
                type="text"
                placeholder="Заголовок"
                value={postNews.title}
                onChange={(e) =>
                  setPostNews({ ...postNews, title: e.target.value })
                }
              />
              <div className="publish-modal-editor">
                <TiptapEditor
                  apiUrl={apiUrl}
                  token={token}
                  initialContent={news?.content}
                  onChange={(json) => setContent(json)}
                />
              </div>

              <div style={{ marginTop: 10 }}>
                <button onClick={handlePublish} className="news-publish-button">
                  📤 Опубликовать
                </button>
              </div>
            </DraggablePublishModal>
          )}
        </>
      )}
      {/* Список новостей */}
      {loading && <Loading className="profile-loading" />}
      {news.length > 0 ? (
        <div className="news-list">
          <div className="tests-ad">
            <div className="tests-item-ad">
              <div className="ad-tests-image">
                <img
                  src="img/onboarding/tests-ad-image.png"
                  alt="tests-ad-image"
                />
              </div>
              <div className="ad-tests-text">
                <h2>Пройдите тесты и проверьте свои знания!</h2>
                <p>Новые тесты по различным темам уже доступны.</p>
                <a
                  href="https://tests.learningportal.ru/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <button>Начать тестирование</button>
                </a>
              </div>
            </div>
          </div>
          {!user.premium?.isActive && (
            <div className="premium-ad">
              <div className="news-item-ad">
                <div className="ad-image">
                  <img
                    src="img/onboarding/premium-main_full.png"
                    alt="premium-ad-image"
                  />
                </div>
                <div className="ad-text">
                  <h2>{text.title}</h2>
                  <p>{text.description}</p>
                  <a href="/premium">
                    <button>Оформить премиум</button>
                  </a>
                </div>
              </div>
            </div>
          )}

          {news.map((item, index) => {
            const isNew = newNews.some((n) => n._id === item._id);
            const showDivider =
              index > 0 &&
              newNews.some((n) => n._id === news[index - 1]._id) &&
              !isNew;
            return (
              <React.Fragment key={item._id}>
                {showDivider && <hr className="news-divider" />}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 25,
                    bounce: 0.5,
                    delay: index * 0.1,
                  }}
                  ref={item._id === highlightId ? highlightRef : null}
                  className={`news-item ${
                    item._id === highlightId ? "highlighted" : ""
                  }`}
                  style={{ paddingTop: isAuthorized ? "45px" : "" }}
                >
                  {editingItem?._id === item._id ? (
                    <div className="edit-form">
                      <AttachmentsInput
                        attachments={editAtt}
                        uploadFile={uploadEdit}
                        removeAttachment={removeEdit}
                        reorder={reorderEdit}
                        onPreview={(url, mime) =>
                          setViewer({ open: true, url, mime })
                        }
                      />
                      <input
                        value={editForm.title}
                        onChange={(e) =>
                          setEditForm({ ...editForm, title: e.target.value })
                        }
                        className="edit-input-field"
                      />
                      <TiptapEditor
                        apiUrl={apiUrl}
                        token={token}
                        initialContent={editForm.content}
                        onChange={(json) =>
                          setEditForm((prev) => ({ ...prev, content: json }))
                        }
                        className="edit-input-textarea"
                      />
                      <input
                        value={editForm.image}
                        placeholder="Ссылка на изображение"
                        onChange={(e) =>
                          setEditForm({ ...editForm, image: e.target.value })
                        }
                        style={{ display: "none" }}
                      />
                      <div className="edit-btns">
                        <button
                          className="news-item-button"
                          onClick={submitEdit}
                        >
                          💾 Сохранить
                        </button>
                        {isAuthorized && (
                          <button onClick={() => handleDelete(item._id)}>
                            Удалить
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3>{item.title}</h3>
                      {item.attachments?.length > 0 && (
                        <div className="news-attachments">
                          {item.attachments.map((att, idx) => (
                            <div
                              key={idx}
                              className="news-attachment-item"
                              onClick={() =>
                                setViewer({
                                  open: true,
                                  url: att.url,
                                  mime: att.mime,
                                })
                              }
                            >
                              {att.mime.startsWith("image/") ? (
                                <img
                                  src={att.url}
                                  alt={`attachment-${idx}`}
                                  className="news-attachment-image"
                                />
                              ) : att.mime.startsWith("video/") ? (
                                <video
                                  src={att.url}
                                  controls
                                  className="news-attachment-video"
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                      <NewsRenderer content={item.content} />
                      <small>
                        Опубликовано:{" "}
                        {new Date(item.createdAt).toLocaleString()}
                        {item.author && ` • ${item.author?.username}`}
                      </small>
                      <LikeButton
                        liked={item.likes?.includes(user._id)}
                        count={item.likes?.length || 0}
                        onClick={() => toggleLike(item._id)}
                      />
                    </div>
                  )}
                  {(isAuthorized ||
                    user.username === item.author?.username) && (
                    <div className="news-control-buttons">
                      {editingItem?._id === item._id ? (
                        <button
                          className="news-item-button"
                          onClick={() => setEditingItem(null)}
                        >
                          ❌ Отмена
                        </button>
                      ) : (
                        <button
                          className="news-item-button"
                          onClick={() => startEdit(item)}
                        >
                          Редактировать
                        </button>
                      )}
                    </div>
                  )}
                </motion.div>
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
      {viewer.open && (
        <DraggableFileViewer
          fileUrl={viewer.url}
          mime={viewer.mime}
          onClose={() => setViewer({ open: false, url: "", mime: "" })}
        />
      )}
    </motion.div>
  );
};

export default MainPage;
